// "Ontology · tap an object": the main object types of courageMFG's own model (ontology.ts) on a grid, joined where
// the model links them. Tapping one highlights it and what it links to, with a one-line description; tap again to reset.
import { useState, type KeyboardEvent } from "react";
import { Link } from "react-router-dom";
import { EDGES, OBJECTS } from "../ontology";

// Which object types to show and where: three columns, commercial at the top, the floor and quality in the middle,
// supply at the bottom left. Every one of them is in OBJECTS; every line below is an edge in EDGES.
const GRID: [id: string, col: number, row: number][] = [
  ["account", 0, 0], ["opportunity", 1, 0], ["quote", 2, 0],
  ["customer", 0, 1], ["sales_order", 1, 1], ["item", 2, 1],
  ["lot", 0, 2], ["issue", 1, 2], ["production_order", 2, 2],
  ["vendor", 0, 3], ["capa", 1, 3], ["work_center", 2, 3],
  ["purchase_order", 0, 4], ["scar", 1, 4],
];

// One plain line for each, in the words of the model's own descriptions.
const PLAIN: Record<string, string> = {
  account: "the company you sell to. When it is qualified, it becomes one or more customers in Business Central.",
  opportunity: "a deal in the pipeline. It carries its quotes, and winning it makes the sales order.",
  quote: "a priced offer on an opportunity, with revisions. It is issued as a sales quote in Business Central.",
  customer: "a sell-to or bill-to record in Business Central.",
  sales_order: "what a customer ordered, and its shipments, in Business Central.",
  item: "a part, a component or a finished good in Business Central.",
  lot: "a tracked quantity of an item. It connects the floor, the supplier and the customer.",
  issue: "a problem or an idea raised on the floor.",
  production_order: "an order to make an item, with its routing and components, in Business Central.",
  vendor: "a supplier in Business Central.",
  capa: "a corrective or preventive action, with the root cause and a check that it worked.",
  work_center: "a line or a cell on the floor, in Business Central.",
  purchase_order: "what you ordered from a vendor, line by line, in Business Central.",
  scar: "a request to a supplier to find and correct the cause of a problem.",
};

const VW = 420, PW = 118, PH = 30, X = (c: number) => 68 + c * 142, Y = (r: number) => 34 + r * 70;
const VH = Y(Math.max(...GRID.map((g) => g[2]))) + 34;

type Node = { id: string; name: string; side: "courage" | "bc"; x: number; y: number; what: string; total: number };
type Line = { a: string; b: string; d: string; planned: boolean };

const NODES: Node[] = GRID.flatMap(([id, c, r]) => {
  const o = OBJECTS.find((x) => x.id === id);
  if (!o) return [];
  const total = new Set(EDGES.filter((e) => e.from === id || e.to === id).map((e) => (e.from === id ? e.to : e.from))).size;
  return [{ id, name: o.name, side: o.side, x: X(c), y: Y(r), what: PLAIN[id] ?? o.what, total }];
});
const byId = new Map(NODES.map((n) => [n.id, n]));

/** A straight line, or a curve around any object it would cross. */
function route(a: Node, b: Node): string {
  const hits = (pt: (t: number) => [number, number]) => {
    for (let i = 1; i < 30; i++) {
      const [x, y] = pt(i / 30);
      if (NODES.some((n) => n !== a && n !== b && Math.abs(x - n.x) < PW / 2 + 4 && Math.abs(y - n.y) < PH / 2 + 4)) return true;
    }
    return false;
  };
  if (!hits((t) => [a.x + (b.x - a.x) * t, a.y + (b.y - a.y) * t])) return `M${a.x},${a.y}L${b.x},${b.y}`;
  const len = Math.hypot(b.x - a.x, b.y - a.y) || 1, nx = -(b.y - a.y) / len, ny = (b.x - a.x) / len;
  for (const off of [-56, 56, -84, 84]) {
    const qx = (a.x + b.x) / 2 + nx * off, qy = (a.y + b.y) / 2 + ny * off;
    const q = (t: number): [number, number] => [(1 - t) ** 2 * a.x + 2 * (1 - t) * t * qx + t * t * b.x, (1 - t) ** 2 * a.y + 2 * (1 - t) * t * qy + t * t * b.y];
    if (!hits(q)) return `M${a.x},${a.y}Q${qx},${qy} ${b.x},${b.y}`;
  }
  return `M${a.x},${a.y}L${b.x},${b.y}`;
}

const LINES: Line[] = (() => {
  const seen = new Set<string>(), out: Line[] = [];
  for (const e of EDGES) {
    const a = byId.get(e.from), b = byId.get(e.to);
    if (!a || !b || a === b) continue;
    const k = [a.id, b.id].sort().join("|");
    if (seen.has(k)) { if (e.status === "live") out.find((l) => [l.a, l.b].sort().join("|") === k)!.planned = false; continue; }
    seen.add(k);
    out.push({ a: a.id, b: b.id, d: route(a, b), planned: e.status !== "live" });
  }
  return out;
})();

const lower = (s: string) => (s === s.toUpperCase() ? s : s.toLowerCase());
const list = (xs: string[]) => (xs.length <= 1 ? xs.join("") : `${xs.slice(0, -1).join(", ")} and ${xs[xs.length - 1]}`);

export function MiningOntology() {
  const [sel, setSel] = useState<string | null>(null);
  const pick = (id: string) => setSel((s) => (s === id ? null : id));
  const key = (e: KeyboardEvent, id: string) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); pick(id); } };
  const linked = new Set(sel ? LINES.filter((l) => l.a === sel || l.b === sel).map((l) => (l.a === sel ? l.b : l.a)) : []);
  const n = sel ? byId.get(sel) : null;
  const names = NODES.filter((x) => linked.has(x.id)).map((x) => lower(x.name));
  const more = n ? n.total - linked.size : 0;
  return (
    <section className={`win onto${sel ? " pick" : ""}`} aria-label="Ontology">
      <div className="win-bar">
        <div className="win-title">Ontology <span>tap an object</span></div>
        <Link className="mini" to="/ontology">Open the full ontology</Link>
      </div>
      <svg viewBox={`0 0 ${VW} ${VH}`} role="group" aria-label={`The main objects in courageMFG and Business Central and how they link: ${NODES.map((x) => lower(x.name)).join(", ")}.`}>
        {LINES.map((l) => (
          <path key={`${l.a}|${l.b}`} className={`ln${l.planned ? " plan" : ""}${sel && (l.a === sel || l.b === sel) ? " act" : ""}`} d={l.d} />
        ))}
        {NODES.map((x) => (
          <g key={x.id} className={`on${x.side === "courage" ? " cm" : ""}${sel === x.id ? " sel act" : linked.has(x.id) ? " act" : ""}`}
            tabIndex={0} role="button" aria-pressed={sel === x.id} aria-label={x.name} onClick={() => pick(x.id)} onKeyDown={(e) => key(e, x.id)}
            onMouseDown={(e) => e.preventDefault() /* a tap shouldn't leave a keyboard focus ring */}>
            <rect x={x.x - PW / 2} y={x.y - PH / 2} width={PW} height={PH} rx={PH / 2} />
            <text x={x.x} y={x.y + 4}>{x.name}</text>
          </g>
        ))}
      </svg>
      <p className="note" aria-live="polite">
        {n ? (
          <><b>{n.name}: </b>{n.what} {names.length ? `Linked to ${list(names)}${more > 0 ? `, and to ${more} more ${more === 1 ? "object" : "objects"} in the full ontology` : ""}.` : ""}</>
        ) : (
          <>Tap an object to see what it is and what it links to. Tinted: kept in courageMFG. White: kept in Business Central. Dashed: a planned link.</>
        )}
      </p>
    </section>
  );
}
